import * as React from 'react';
import { FlaskConical, Hourglass, ShieldCheck, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';
import { COMPANY_ENVIRONMENT_LABELS, type CompanyEnvironment } from './company-environment';

/**
 * Contrato: `docs/FRONTEND_COMPONENT_INVENTORY.md` §5.7 — vistas 18 (listado
 * listing) and 35 (a sandbox company's summary), plus `AppShell`'s identity
 * strip (§5.5), where `environment` is a MANDATORY prop.
 *
 * It is ONE, not two: `SandboxBadge` does not exist as a separate component,
 * even though §7.3 names it on the same row as this. Both consumers ask the
 * same question — which of the three environments am I in — and an alias with
 * identical behaviour is exactly how, one day, the sandbox look changes under
 * one of the two names and not the other. `<EnvironmentBadge
 * environment="sandbox" />` es sandbox.
 *
 * It does NOT derive the environment (it RECEIVES it): the derivation already
 * exists and is written to throw on the impossible fourth case (`isSandbox &&
 * isProduction`) in `deriveCompanyEnvironment()`, in `company-select.tsx`. Two
 * derivations is how a badge and a selector end up saying different things
 * about the same company — the failure this pair exists to avoid.
 */
export interface EnvironmentBadgeProps {
  environment: CompanyEnvironment;
  /** `compact` for a table cell (18); `full` for `AppShell`'s strip, where it
   *  is the first line of defence. */
  size?: 'compact' | 'full';
  /**
   * `badge` (the default) is the filled `StatusBadge`. `plain` is the same
   * icon and the same word with no fill, for a surface that already paints a
   * background of its own — `CompanySelect`'s list, where the active row is
   * highlighted in `action-subtle` and a filled badge on top of it was two
   * tints stacked, with the badge losing contrast on exactly the row that
   * matters. The icon still carries the environment without colour.
   */
  appearance?: 'badge' | 'plain';
  className?: string;
}

interface EnvironmentPresentation {
  tone: StatusTone;
  variant: 'outline' | 'soft';
  icon: LucideIcon;
}

/**
 * Tones and icons per environment — a contract decision, not a detail:
 *
 * - `production` is `info`/`soft`, NEVER `danger`: in this app red means
 *   "folio consumed and rejected" (§6, decision 2), and painting the normal
 *   working environment red trains people to ignore red exactly where it most
 *   matters that they do not.
 * - `certification` is `neutral`: a company not yet authorised is a state that
 *   lasts weeks — as a permanent `warning` it stops reading as a warning at all.
 * - `sandbox` is `neutral` with a FlaskConical (C-8): it is the one environment
 *   that MANUFACTURES complete documents (stamped, signed, with a PDF) that
 *   never touch the SII. It is told apart by its flask icon and its «Sandbox»
 *   label, not by amber: `warning` is reserved for "needs attention", and a
 *   sandbox is not a problem.
 *
 * All three are `soft` (2026-11 top-bar critique). Certification and sandbox
 * used to be `outline`, and in the strip a full-radius pill with a drawn
 * border, at button height, sat to the left of the company selector looking
 * exactly like a secondary button: people read it as something to click. A
 * filled, borderless tag reads as a label. The `outline` = "in transit"
 * semantics of §5.1 still hold for workflow statuses; an environment is not a
 * step in a workflow, it is a property of the company.
 *
 * Each also carries an icon of a different silhouette — never the same glyph
 * merely tinted another colour — so the three are told apart without relying on
 * colour (WCAG 1.4.1): it is precisely the badge a colour-blind operator reads
 * before
 * de emitir.
 */
const ENVIRONMENT_PRESENTATION: Record<CompanyEnvironment, EnvironmentPresentation> = {
  production: { tone: 'info', variant: 'soft', icon: ShieldCheck },
  certification: { tone: 'neutral', variant: 'soft', icon: Hourglass },
  sandbox: { tone: 'neutral', variant: 'soft', icon: FlaskConical },
};

/**
 * The active company's environment badge. It composes `StatusBadge` (§5.1),
 * which already anticipates it in its comments.
 *
 * What it does NOT do: it does not derive the environment, does not fetch the
 * active company, does not change it (that is `CompanySelect`, the second line
 * of defence) and hides or disables nothing by environment.
 */
export function EnvironmentBadge({
  environment,
  size = 'compact',
  appearance = 'badge',
  className,
}: EnvironmentBadgeProps): React.JSX.Element {
  const { tone, variant, icon } = ENVIRONMENT_PRESENTATION[environment];

  if (appearance === 'plain') {
    const Icon = icon;
    return (
      <span
        className={cn(
          'inline-flex items-center gap-stack-sm whitespace-nowrap text-caption font-medium',
          // Production keeps its `info` colour, as the badge does; the other two
          // are neutral and told apart by icon and word.
          tone === 'info' ? 'text-info' : 'text-fg-muted',
          className,
        )}
      >
        <Icon aria-hidden="true" className="size-icon-sm" />
        <span>{COMPANY_ENVIRONMENT_LABELS[environment]}</span>
      </span>
    );
  }

  return (
    <StatusBadge
      tone={tone}
      variant={variant}
      icon={icon}
      className={cn(size === 'full' && 'px-inset py-1 text-body-s', className)}
    >
      {COMPANY_ENVIRONMENT_LABELS[environment]}
    </StatusBadge>
  );
}
